import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { App as AntApp, Input, Segmented, Switch, Button } from 'antd';
import AppBar from '../components/AppBar';
import InfoNotes from '../components/InfoNotes';
import { RM } from '../components/kit';
import { computeMortgage, num, FTB_PRICE_CAP } from '../lib/loanCalc';

// Mortgage calculator — Pre/New Launch vs Subsale, Malaysian statutory scales
// (SRO 2023 legal fees, MOT + loan stamp duty, Budget-2026 FTB exemption,
// subsale bank-valuation scale). Pure client-side, nothing saved.

const SQFT_PER_SQM = 10.7639;

const DEFAULTS = {
  price: '', ratePct: '4.00', years: '30',
  downMode: '%', downValue: '', discountPct: '',
  freeSpaLegal: false, freeSpaStamp: false, freeLoanLegal: false, freeLoanStamp: false,
  otherFinancedFees: '', financeDocCosts: false,
  firstTimeBuyer: false,
  areaUnit: 'sqft', area: '', rent: '', utilities: '',
};

export default function MortgagePage() {
  const nav = useNavigate();
  const { message } = AntApp.useApp();
  const [mode, setMode] = useState('NEW'); // 'NEW' | 'SUBSALE'
  const [f, setF] = useState(DEFAULTS);
  const patch = (p) => setF((x) => ({ ...x, ...p }));
  const reset = () => setF(DEFAULTS);

  // Calculate → dedicated result page (loan summary, cash required, price/area,
  // investment, amortization + share in its ⋮ menu).
  const calculate = () => {
    if (!(num(f.price) > 0)) { message.warning('Enter the purchase price'); return; }
    if (!(num(f.ratePct) > 0) || !(num(f.years) > 0)) { message.warning('Enter the interest rate and loan tenure'); return; }
    nav('/tools/mortgage/result', { state: { mode, f } });
  };

  const r = useMemo(
    () =>
      computeMortgage({
        mode,
        price: f.price,
        ratePct: f.ratePct,
        years: f.years,
        downMode: f.downMode,
        downValue: f.downValue,
        discountPct: f.discountPct,
        freeSpaLegal: f.freeSpaLegal,
        freeSpaStamp: f.freeSpaStamp,
        freeLoanLegal: f.freeLoanLegal,
        freeLoanStamp: f.freeLoanStamp,
        otherFinancedFees: f.otherFinancedFees,
        financeDocCosts: f.financeDocCosts,
        firstTimeBuyer: f.firstTimeBuyer,
        // yields are computed per sqft internally; convert sqm input
        area: f.areaUnit === 'sqm' ? num(f.area) * SQFT_PER_SQM : f.area,
        rent: f.rent,
        utilities: f.utilities,
      }),
    [mode, f]
  );

  const [infoOpen, setInfoOpen] = useState(false);
  const info = () => setInfoOpen(true);
  const INFO_PAGES = [
    { body: 'Calculate monthly installment, entry cost, price/ area, rental yield, cash flow and many more. Learn more on home ownership cost, avoid unwanted surprises and monthly cash flow in short. Note, do not over leverage.' },
    { title: 'Scales', body: 'Legal fees follow the SRO 2023 scale (1.25% on the first RM500k, minimum RM500). Stamp duty per current MOT and loan tiers; loan agreement duty 0.5%. First-time-buyer exemption per Budget 2026 (homes \u2264 RM500k). Estimates only \u2014 nothing is saved.' },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <AppBar back title="Mortgage" right={<InfoDot onClick={info} />} />

      <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Segmented
          block
          options={[{ label: 'Pre/ New Launch', value: 'NEW' }, { label: 'Subsale', value: 'SUBSALE' }]}
          value={mode}
          onChange={setMode}
        />

        <Field label="Purchase Price">
          <MoneyInput value={f.price} onChange={(v) => patch({ price: v })} placeholder="0" />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="Interest Rate (%)">
            <NumInput value={f.ratePct} onChange={(v) => patch({ ratePct: v })} placeholder="4.00" />
          </Field>
          <Field label="Loan Tenure (Year)">
            <NumInput value={f.years} onChange={(v) => patch({ years: v })} placeholder="30" />
          </Field>
        </div>

        {/* ── OPTIONAL ─────────────────────────────────────────────── */}
        <Section title="OPTIONAL">
          <Field
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                Down Payment
                <Segmented size="small" options={['%', '$']} value={f.downMode} onChange={(v) => patch({ downMode: v })} />
              </span>
            }
          >
            {f.downMode === '%' ? (
              <NumInput value={f.downValue} onChange={(v) => patch({ downValue: v })} placeholder="0.00%" />
            ) : (
              <MoneyInput value={f.downValue} onChange={(v) => patch({ downValue: v })} placeholder="0" />
            )}
          </Field>

          {mode === 'NEW' && (
            <>
              <Field label="Developer Discount (%)">
                <NumInput value={f.discountPct} onChange={(v) => patch({ discountPct: v })} placeholder="0.00%" />
              </Field>
              <ToggleRow label="Free SPA Legal Fee" checked={f.freeSpaLegal} onChange={(v) => patch({ freeSpaLegal: v })} />
              <ToggleRow label="Free SPA Stamp Duty (MOT/ DOA)" checked={f.freeSpaStamp} onChange={(v) => patch({ freeSpaStamp: v })} />
              <ToggleRow label="Free Loan Legal Fee" checked={f.freeLoanLegal} onChange={(v) => patch({ freeLoanLegal: v })} />
              <ToggleRow label="Free Loan Stamp Duty" checked={f.freeLoanStamp} onChange={(v) => patch({ freeLoanStamp: v })} />
            </>
          )}

          {mode === 'SUBSALE' && (
            <>
              <Field label="Other Financed Fees">
                <MoneyInput value={f.otherFinancedFees} onChange={(v) => patch({ otherFinancedFees: v })} placeholder="0" />
              </Field>
              <ToggleRow label="Finance Loan Doc & Valuation Costs*" checked={f.financeDocCosts} onChange={(v) => patch({ financeDocCosts: v })} />
            </>
          )}

          <ToggleRow label="First Time Home Buyer" checked={f.firstTimeBuyer} onChange={(v) => patch({ firstTimeBuyer: v })} />
          {r.ftbBlocked && (
            <div style={{ fontSize: 11.5, color: 'var(--warn)', marginTop: -6 }}>
              Stamp-duty exemption applies to homes ≤ {RM(FTB_PRICE_CAP)} only — not applied.
            </div>
          )}
        </Section>

        {/* ── OTHER ────────────────────────────────────────────────── */}
        <Section title="OTHER">
          <Field
            label={
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                Area
                <Segmented size="small" options={['sqft', 'sqm']} value={f.areaUnit} onChange={(v) => patch({ areaUnit: v })} />
              </span>
            }
          >
            <NumInput value={f.area} onChange={(v) => patch({ area: v })} placeholder="0" />
          </Field>
          <Field label="Monthly Rental Income">
            <MoneyInput value={f.rent} onChange={(v) => patch({ rent: v })} placeholder="0" />
          </Field>
          <Field label="Monthly Utilities & Fees">
            <MoneyInput value={f.utilities} onChange={(v) => patch({ utilities: v })} placeholder="0" />
          </Field>
        </Section>
      </div>

      <div style={{ display: 'flex', gap: 12, padding: '12px 12px max(12px, env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', background: 'var(--surface)', flex: 'none' }}>
        <Button block size="large" onClick={reset}>Reset</Button>
        <Button block size="large" type="primary" onClick={calculate}>Calculate</Button>
      </div>

      <InfoNotes open={infoOpen} onClose={() => setInfoOpen(false)} title="Mortgage" pages={INFO_PAGES} />
    </div>
  );
}

// ─── shared bits ──────────────────────────────────────────────────────
export const InfoDot = ({ onClick }) => (
  <div onClick={onClick} role="button" style={{ width: 34, height: 34, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)' }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
      <circle cx="12" cy="12" r="9" /><line x1="12" y1="11" x2="12" y2="16" /><circle cx="12" cy="7.6" r="0.4" fill="currentColor" />
    </svg>
  </div>
);

const Field = ({ label, children }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
    <label style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--muted)' }}>{label}</label>
    {children}
  </div>
);

const Section = ({ title, children }) => (
  <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
    <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--primary)' }}>{title}</div>
    {children}
  </div>
);

const ToggleRow = ({ label, checked, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
    <span style={{ fontSize: 13 }}>{label}</span>
    <Switch checked={checked} onChange={onChange} />
  </div>
);

const Block = ({ title, children }) => (
  <div style={{ border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
    <div style={{ padding: '13px 15px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', background: 'var(--surface2)' }}>{title}</div>
    {children}
  </div>
);

const Row = ({ label, value, strong, dim, tone }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '13px 15px', borderTop: '1px solid var(--border)', fontSize: 12.5, background: strong ? 'var(--chip)' : undefined }}>
    <span style={{ color: dim ? 'var(--muted)' : strong ? 'var(--primary)' : 'var(--text)', fontWeight: strong ? 600 : 400 }}>{label}</span>
    <b style={{ color: tone || (dim ? 'var(--muted)' : strong ? 'var(--primary)' : 'var(--text)'), textDecoration: dim ? 'line-through' : undefined, whiteSpace: 'nowrap' }}>{value}</b>
  </div>
);

// numeric-only inputs (mobile keypads)
export const NumInput = ({ value, onChange, placeholder }) => (
  <Input inputMode="decimal" value={value} placeholder={placeholder}
    onChange={(e) => { const v = e.target.value; if (/^\d*\.?\d*$/.test(v)) onChange(v); }} />
);
// Displays with thousands separators (10,000) while the stored value stays a raw
// numeric string — shared by every RM field across the Tools calculators.
const addCommas = (s) => {
  const [int, dec] = String(s).split('.');
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',') + (dec !== undefined ? `.${dec}` : '');
};
export const MoneyInput = ({ value, onChange, placeholder }) => (
  <Input inputMode="numeric" prefix={<span style={{ color: 'var(--muted)', fontSize: 12.5 }}>RM</span>} value={value ? addCommas(value) : ''} placeholder={placeholder}
    onChange={(e) => { const v = e.target.value.replace(/,/g, ''); if (/^\d*\.?\d*$/.test(v)) onChange(v); }} />
);
